import React, { useState, useEffect, useMemo, useRef } from 'react';
import { navigate } from './App';
import { useStore, useStoreProducts, useStoreCategories, useStoreFAQ } from './hooks/useStoreApi';
import { OfferingTypeProvider } from './hooks/useVocab';
import { getVocab } from './utils/storeVocabulary';
import { getEffectiveUnitPrice } from './utils/pricing';
import { CheckoutForm } from './CheckoutForm';
import { OrderConfirmation } from './OrderConfirmation';
import { CartModal } from './components/CartModal';
import { ProductDetailModal } from './components/ProductDetailModal';
import { PaidOptionsModal } from './components/PaidOptionsModal';
import { Spinner } from './components/ui/Spinner';
import { ShareMenu } from './components/ui/ShareMenu';
import { Button } from './components/ui/Button';
import {
    StorePopup,
    StoreTopBar,
    StoreCover,
    StoreTabs,
    StoreClosedBanner,
    isStoreClosed,
    StoreInfoTab,
    StoreFaqTab,
    StoreProductsTab,
    StoreFloatingCart,
    ClubRegisterModal,
} from './components/store/view';
import { Gift } from 'lucide-react';
import { getShareUrl } from './utils/share';
import { getStoreDesignStyle, getStoreDesignAttrs } from './themes/applyStoreDesign';
import { hasClubBenefit, clubBenefitHeadline } from './utils/clubBenefit';

const PAGE_SIZE = 24;

/**
 * Storefront page. Owns store + cart + UI-mode state and delegates rendering
 * to the smaller components in `components/store/view/`.
 */
export const StoreView = ({ storeSlug, chatId }) => {
    // --- UI state
    const [selectedCategory, setSelectedCategory] = useState(null);
    const [activeTab, setActiveTab] = useState('products');
    const [searchTerm, setSearchTerm] = useState('');
    const [sortBy, setSortBy] = useState('popular');
    const [page, setPage] = useState(1);
    const [showPopup, setShowPopup] = useState(false);
    const [showShareMenu, setShowShareMenu] = useState(false);
    const [showClubModal, setShowClubModal] = useState(false);
    // Track portrait vs landscape so we can serve the matching store
    // background image (mobile/portrait vs desktop/landscape).
    const [isPortrait, setIsPortrait] = useState(() =>
        typeof window !== 'undefined' && window.matchMedia
            ? window.matchMedia('(orientation: portrait)').matches
            : false
    );
    useEffect(() => {
        if (typeof window === 'undefined' || !window.matchMedia) return;
        const mq = window.matchMedia('(orientation: portrait)');
        const onChange = (e) => setIsPortrait(e.matches);
        mq.addEventListener?.('change', onChange);
        // Safari < 14 fallback
        mq.addListener?.(onChange);
        return () => {
            mq.removeEventListener?.('change', onChange);
            mq.removeListener?.(onChange);
        };
    }, []);
    const sentinelRef = useRef(null);
    const isRTL = true;

    // --- Cart + checkout state
    const [cart, setCart] = useState([]);
    const [showCart, setShowCart] = useState(false);
    const [checkoutStep, setCheckoutStep] = useState('cart'); // cart | checkout | confirmation
    const [orderData, setOrderData] = useState(null);
    const [selectedProduct, setSelectedProduct] = useState(null);
    const [productForPaidOptions, setProductForPaidOptions] = useState(null);

    // --- Data
    const { data: store, loading: storeLoading, error: storeError } = useStore(storeSlug, chatId);
    const { data: products, loading: productsLoading, error: productsError, refetch: refetchProducts } = useStoreProducts(storeSlug);
    const { data: categories, loading: categoriesLoading, error: categoriesError } = useStoreCategories(storeSlug);
    const { data: faqs, loading: faqsLoading, error: faqsError } = useStoreFAQ(storeSlug);

    // --- URL <-> filter sync
    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        const cat = params.get('category');
        const search = params.get('search');
        const sort = params.get('sort');
        if (cat) setSelectedCategory(cat);
        if (search) setSearchTerm(search);
        if (sort) setSortBy(sort);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [storeSlug]);

    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        selectedCategory ? params.set('category', selectedCategory) : params.delete('category');
        searchTerm ? params.set('search', searchTerm) : params.delete('search');
        sortBy !== 'popular' ? params.set('sort', sortBy) : params.delete('sort');
        const newQuery = params.toString();
        const newUrl = `${window.location.pathname}${newQuery ? `?${newQuery}` : ''}`;
        window.history.replaceState(null, '', newUrl);
    }, [selectedCategory, searchTerm, sortBy]);

    // --- Infinite scroll sentinel
    useEffect(() => {
        const el = sentinelRef.current;
        if (!el) return;
        const observer = new IntersectionObserver((entries) => {
            entries.forEach((entry) => {
                if (entry.isIntersecting) setPage((p) => p + 1);
            });
        }, { threshold: 1 });
        observer.observe(el);
        return () => observer.disconnect();
    }, [sentinelRef, products, searchTerm, selectedCategory, sortBy]);

    // --- Category maps
    const categoryMap = useMemo(() => {
        const map = {};
        (categories || []).forEach((cat) => { map[cat.category_id] = cat.name; });
        return map;
    }, [categories]);

    const categoryOrderMap = useMemo(() => {
        const orderMap = {};
        (categories || []).forEach((cat, index) => {
            const order = (cat.sort_order !== undefined && cat.sort_order !== null) ? Number(cat.sort_order) : index;
            orderMap[cat.category_id] = isNaN(order) ? index : order;
        });
        return orderMap;
    }, [categories]);

    // --- Products filter + sort
    const filteredProducts = useMemo(() => {
        let list = [...(products || [])];
        list = list.filter((p) => p?.available === true);
        if (selectedCategory) list = list.filter((p) => p.category_id === selectedCategory);
        if (searchTerm) {
            const term = searchTerm.toLowerCase();
            list = list.filter((p) =>
                (p.name || '').toLowerCase().includes(term) ||
                (p.description || '').toLowerCase().includes(term),
            );
        }

        switch (sortBy) {
            case 'price_asc':  list.sort((a, b) => (a.price || 0) - (b.price || 0)); break;
            case 'price_desc': list.sort((a, b) => (b.price || 0) - (a.price || 0)); break;
            case 'new':        list.sort((a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0)); break;
            case 'name':       list.sort((a, b) => (a.name || '').localeCompare(b.name || '')); break;
            default: break;
        }

        if (!selectedCategory && sortBy === 'popular') {
            list.sort((a, b) => {
                const catA = a.category_id || '';
                const catB = b.category_id || '';
                if (!catA && catB) return 1;
                if (catA && !catB) return -1;
                if (catA === catB) {
                    return (a.sort_order ?? Infinity) - (b.sort_order ?? Infinity);
                }
                const orderA = categoryOrderMap[catA] ?? Number.MAX_SAFE_INTEGER;
                const orderB = categoryOrderMap[catB] ?? Number.MAX_SAFE_INTEGER;
                if (orderA !== orderB) return orderA - orderB;
                const nameA = categoryMap[catA] || catA;
                const nameB = categoryMap[catB] || catB;
                return nameA.localeCompare(nameB);
            });
        }
        return list;
    }, [products, selectedCategory, searchTerm, sortBy, categoryMap, categoryOrderMap]);

    const visibleProducts = useMemo(() => filteredProducts.slice(0, page * PAGE_SIZE), [filteredProducts, page]);
    const hasMore = visibleProducts.length < filteredProducts.length;

    // --- Cart helpers
    const arePaidOptionsEqual = (opts1, opts2) => {
        const arr1 = opts1 || [];
        const arr2 = opts2 || [];
        if (arr1.length !== arr2.length) return false;
        const sorted1 = [...arr1].sort((a, b) => a.name.localeCompare(b.name));
        const sorted2 = [...arr2].sort((a, b) => a.name.localeCompare(b.name));
        return sorted1.every((opt, idx) => opt.name === sorted2[idx].name && opt.price === sorted2[idx].price);
    };

    const addToCart = (product) => {
        if (isStoreClosed(store)) return;
        setCart((prevCart) => {
            // Effective cap = min(product inventory, category inventory) for whichever are enabled.
            // Note: category cap is per-product here, not aggregated across all category siblings in cart —
            // the backend re-validates aggregated category usage at checkout.
            const productInvEnabled = product.inventory?.enabled;
            const categoryInvEnabled = product.category_inventory?.enabled;
            const parseQty = (v) => {
                if (v === undefined || v === null || v === '') return undefined;
                const n = parseInt(v, 10);
                return isNaN(n) ? undefined : n;
            };
            const productQty = productInvEnabled ? parseQty(product.inventory?.quantity ?? product.stock_quantity) : undefined;
            const categoryQty = categoryInvEnabled ? parseQty(product.category_inventory?.quantity) : undefined;
            const limits = [productQty, categoryQty].filter((n) => typeof n === 'number');
            const inventoryQty = limits.length > 0 ? Math.min(...limits) : undefined;
            const inventoryEnabled = productInvEnabled || categoryInvEnabled;
            const existing = prevCart.find((item) =>
                item.product_id === product.product_id &&
                arePaidOptionsEqual(item.selectedPaidOptions, product.selectedPaidOptions),
            );
            const existingQty = existing ? existing.quantity : 0;
            if (inventoryEnabled && typeof inventoryQty === 'number') {
                if (inventoryQty <= 0 || existingQty >= inventoryQty) return prevCart;
            }
            if (existing) {
                return prevCart.map((item) =>
                    (item.product_id === product.product_id &&
                        arePaidOptionsEqual(item.selectedPaidOptions, product.selectedPaidOptions))
                        ? { ...item, quantity: item.quantity + 1 }
                        : item,
                );
            }
            return [...prevCart, { ...product, quantity: 1 }];
        });
    };

    const removeFromCart = (productId, selectedPaidOptions = null) => {
        if (isStoreClosed(store)) return;
        setCart((prevCart) => {
            const existing = prevCart.find((item) =>
                item.product_id === productId &&
                (selectedPaidOptions === null || arePaidOptionsEqual(item.selectedPaidOptions, selectedPaidOptions)),
            );
            if (existing && existing.quantity > 1) {
                return prevCart.map((item) =>
                    (item.product_id === productId &&
                        (selectedPaidOptions === null || arePaidOptionsEqual(item.selectedPaidOptions, selectedPaidOptions)))
                        ? { ...item, quantity: item.quantity - 1 }
                        : item,
                );
            }
            return prevCart.filter((item) =>
                !(item.product_id === productId &&
                    (selectedPaidOptions === null || arePaidOptionsEqual(item.selectedPaidOptions, selectedPaidOptions))),
            );
        });
    };

    const getTotalItems = () => cart.reduce((sum, item) => sum + item.quantity, 0);
    const getItemTotal = (item) => {
        const base = getEffectiveUnitPrice(item) * item.quantity;
        const optionsTotal = (item.selectedPaidOptions || []).reduce((sum, opt) => sum + (parseFloat(opt.price) * item.quantity), 0);
        return base + optionsTotal;
    };
    const getTotalPrice = () => cart.reduce((sum, item) => sum + getItemTotal(item), 0);

    // --- Reactive side-effects
    useEffect(() => {
        if (isStoreClosed(store) && cart.length > 0) setCart([]);
    }, [store?.status, store?.is_active, store?.open]);

    useEffect(() => {
        if (store && (store.popup_title || store.popup_description)) setShowPopup(true);
    }, [store?.popup_title, store?.popup_description]);

    useEffect(() => {
        if (!showPopup) return;
        const prev = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        return () => { document.body.style.overflow = prev; };
    }, [showPopup]);

    // --- AI chat bridge ------------------------------------------------
    // Flag the global AIChatBar that we're inside a storefront so it flips
    // into order-taking mode (mirrors the admin bridge in StoreAdmin).
    useEffect(() => {
        window.__mekomit_store_active = true;
        return () => {
            window.__mekomit_store_active = false;
            window.__mekomit_store_context = null;
        };
    }, []);

    // Keep the catalog snapshot the chat sends to the order matcher in sync.
    useEffect(() => {
        if (!store) return;
        const catalog = (products || [])
            .filter((p) => p?.available === true)
            .slice(0, 200)
            .map((p) => ({
                product_id: p.product_id,
                name: p.name,
                description: typeof p.description === 'string' ? p.description.slice(0, 240) : '',
                price: p.price,
                category: categoryMap[p.category_id] || undefined,
                image: (p.images && p.images[0]) || undefined,
                paid_options: Array.isArray(p.paid_options)
                    ? p.paid_options.map((o) => ({ name: o.name, price: o.price }))
                    : [],
            }));
        window.__mekomit_store_context = {
            store_name: store.name,
            slug: store.slug || storeSlug,
            is_closed: isStoreClosed(store),
            products: catalog,
        };
    }, [store, products, categoryMap, storeSlug]);

    // The chat dispatches this when the customer approves an AI-built order.
    // Resolve each line to a real product, attach the matched paid options,
    // merge into the cart, and open the cart for final review.
    useEffect(() => {
        const onAiOrder = (e) => {
            const items = e?.detail?.items;
            if (!Array.isArray(items) || items.length === 0) return;
            if (isStoreClosed(store)) return;
            let added = false;
            items.forEach(({ product_id, quantity, paid_options }) => {
                const product = (products || []).find((p) => p.product_id === product_id);
                if (!product) return;
                const withOptions = (paid_options && paid_options.length)
                    ? { ...product, selectedPaidOptions: paid_options }
                    : product;
                const qty = Math.max(1, parseInt(quantity, 10) || 1);
                for (let i = 0; i < qty; i += 1) addToCart(withOptions);
                added = true;
            });
            if (added) setShowCart(true);
        };
        window.addEventListener('mekomit:store-add-to-cart', onAiOrder);
        return () => window.removeEventListener('mekomit:store-add-to-cart', onAiOrder);
    }, [products, store]);

    // --- Tab definitions
    const t = getVocab(store?.offering_type);
    const tabs = [
        { id: 'products', label: t.catalog },
        { id: 'about',    label: 'אודות' },
        { id: 'faq',      label: 'שאלות נפוצות', visible: !!(faqs && faqs.length > 0) },
        { id: 'cancellation', label: 'מדיניות ביטול', visible: !!(store?.cancellation_policy && store.cancellation_policy.trim()) },
    ];

    // --- Early returns
    if (storeLoading) {
        return (
            <div className="flex items-center justify-center min-h-screen bg-surface-base">
                <div className="text-center space-y-3">
                    <Spinner size="xl" />
                    <p className="text-ink-muted font-semibold">טוען חנות...</p>
                </div>
            </div>
        );
    }

    if (storeError || !store) {
        return (
            <div className="flex items-center justify-center min-h-screen bg-surface-base p-4">
                <div className="surface-elevated p-6 text-center max-w-md">
                    <p className="heading-md text-danger-700 mb-2">שגיאה בטעינת החנות</p>
                    <p className="text-danger-700/80">{storeError || 'החנות לא נמצאה'}</p>
                </div>
            </div>
        );
    }

    // --- Render
    const shareUrl = (() => {
        try { return getShareUrl('store', storeSlug); } catch { return `https://hamekomit.com/${storeSlug}`; }
    })();

    // Customers club: guests must log in; logged-in users join in one click
    // using the details already on their account.
    const isClubGuest = !chatId || chatId === 'guest';
    const clubUser = {
        id: chatId,
        name: (typeof localStorage !== 'undefined' && localStorage.getItem('user_name')) || '',
        email: (typeof localStorage !== 'undefined' && localStorage.getItem('email')) || '',
    };
    const handleClubLogin = () => {
        try {
            // Dedicated key (not `redirectAfterLogin`, which the app overwrites
            // on the '/' load) so login can bring the user back to this store.
            sessionStorage.setItem('loginReturnUrl', window.location.pathname + window.location.search);
            // Null chat_id is required for the login screen. Clear both the
            // localStorage value and the cookie fallback the app reads on boot.
            localStorage.removeItem('chat_id');
            document.cookie = 'mekomitChatId=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/';
        } catch { /* ignore storage errors */ }
        // Store URLs are treated as "shared links" and bypass the login screen,
        // so we must leave the store path. Root (pathname '/') shows LoginPrompt.
        window.location.href = '/';
    };

    const designStyle = getStoreDesignStyle(store);
    const designAttrs = getStoreDesignAttrs(store);

    // Admin-uploaded full-store backdrop. When present it replaces the
    // theme texture and spans the whole viewport behind all content.
    // Portrait/mobile and landscape/desktop can each have their own image;
    // whichever exists is used, preferring the orientation-matched one.
    const bgLandscape = store.background_image_url;
    const bgPortrait = store.background_image_portrait_url;
    const bgImageUrl = isPortrait
        ? (bgPortrait || bgLandscape)
        : (bgLandscape || bgPortrait);
    const rootStyle = bgImageUrl
        ? {
            ...designStyle,
            backgroundImage: `url(${JSON.stringify(bgImageUrl)})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            backgroundRepeat: 'no-repeat',
            backgroundAttachment: 'fixed',
            backgroundBlendMode: 'normal',
        }
        : designStyle;

    return (
        <OfferingTypeProvider offeringType={store?.offering_type}>
        <div
            dir="rtl"
            className="min-h-screen flex flex-col bg-surface-base text-ink"
            {...designAttrs}
            style={rootStyle}
        >
            <StorePopup
                isOpen={showPopup}
                title={store.popup_title}
                description={store.popup_description}
                style={store.popup_style}
                onClose={() => setShowPopup(false)}
            />

            <StoreTopBar
                isAdmin={store.is_owner || store.is_co_admin}
                onAdminClick={() => navigate('store-admin', `slug=${storeSlug}${chatId ? `&user_id=${encodeURIComponent(chatId)}` : ''}`)}
            />

            <StoreCover store={store} />

            <StoreTabs
                activeTab={activeTab}
                onChange={setActiveTab}
                tabs={tabs}
                store={store}
                onShare={() => setShowShareMenu(true)}
            />

            <StoreClosedBanner store={store} />

            <main className="flex-1 w-full max-w-5xl mx-auto px-2 sm:px-4 py-6 pt-[60px]">
                {hasClubBenefit(store.club_benefit) && (
                    <button
                        type="button"
                        onClick={() => setShowClubModal(true)}
                        className="group w-full mb-5 flex items-center gap-3 rounded-2xl border border-brand-200 bg-gradient-to-l from-brand-50 to-secondary-50 px-4 py-3.5 text-start shadow-sm hover:shadow-md hover:border-brand-300 transition-all"
                    >
                        <span className="shrink-0 inline-flex items-center justify-center w-11 h-11 rounded-full bg-brand-500 text-ink-inverse">
                            <Gift className="w-6 h-6" />
                        </span>
                        <span className="flex-1 min-w-0">
                            <span className="block font-bold text-ink">הצטרפו למועדון הלקוחות</span>
                            <span className="block text-sm text-ink-muted truncate">{clubBenefitHeadline(store.club_benefit)}</span>
                        </span>
                        <span className="shrink-0 text-sm font-semibold text-brand-700 group-hover:text-brand-800">
                            להרשמה ←
                        </span>
                    </button>
                )}

                {activeTab === 'products' && (
                    <StoreProductsTab
                        products={products}
                        visibleProducts={visibleProducts}
                        filteredProducts={filteredProducts}
                        productsLoading={productsLoading}
                        productsError={productsError}
                        categoriesError={categoriesError}
                        categoriesLoading={categoriesLoading}
                        categories={categories}
                        categoryMap={categoryMap}
                        selectedCategory={selectedCategory}
                        searchTerm={searchTerm}
                        sortBy={sortBy}
                        onSearchChange={(v) => { setSearchTerm(v); setPage(1); }}
                        onSortChange={(v) => { setSortBy(v); setPage(1); }}
                        onCategorySelect={(cat) => { setSelectedCategory(cat); setPage(1); }}
                        onClearFilters={() => { setSelectedCategory(null); setSearchTerm(''); setSortBy('popular'); setPage(1); }}
                        onRefetchProducts={refetchProducts}
                        onAddToCart={addToCart}
                        onRemoveFromCart={removeFromCart}
                        onSelectProduct={(p) => setSelectedProduct(p)}
                        onRequestPaidOptions={(p) => setProductForPaidOptions(p)}
                        cart={cart}
                        hasMore={hasMore}
                        onLoadMore={() => setPage((p) => p + 1)}
                        sentinelRef={sentinelRef}
                        rtl={isRTL}
                    />
                )}

                {activeTab === 'about' && (
                    <StoreInfoTab title={`אודות ${t.storeNounDefinite}`} content={store.about} imageUrl={store.about_image_url} />
                )}

                {activeTab === 'faq' && (
                    <StoreFaqTab faqs={faqs} loading={faqsLoading} error={faqsError} />
                )}

                {activeTab === 'cancellation' && (
                    <StoreInfoTab
                        title="מדיניות ביטול רכישה"
                        content={store.cancellation_policy}
                        emptyMessage="אין מדיניות ביטול זמינה כרגע."
                    />
                )}
            </main>

            <StoreFloatingCart
                store={store}
                cart={cart}
                showCart={showCart}
                selectedProduct={selectedProduct}
                onOpenCart={() => setShowCart(true)}
                getTotalItems={getTotalItems}
                getTotalPrice={getTotalPrice}
            />

            <CartModal
                visible={showCart && !isStoreClosed(store)}
                cart={cart}
                checkoutStep={checkoutStep}
                orderData={orderData}
                storeSlug={storeSlug}
                store={store}
                onAddToCart={addToCart}
                onRemoveFromCart={removeFromCart}
                setCheckoutStep={setCheckoutStep}
                setOrderData={setOrderData}
                setCart={setCart}
                onClose={() => {
                    setShowCart(false);
                    if (checkoutStep === 'confirmation') {
                        setCart([]);
                        setOrderData(null);
                    }
                    setCheckoutStep('cart');
                }}
            />

            {selectedProduct && (
                <ProductDetailModal
                    product={selectedProduct}
                    cart={cart}
                    onAddToCart={addToCart}
                    onRemoveFromCart={removeFromCart}
                    onClose={() => setSelectedProduct(null)}
                    onContinueToCart={() => {
                        setSelectedProduct(null);
                        setShowCart(true);
                    }}
                />
            )}

            {productForPaidOptions && (
                <PaidOptionsModal
                    product={productForPaidOptions}
                    onConfirm={(productWithOptions) => addToCart(productWithOptions)}
                    onClose={() => setProductForPaidOptions(null)}
                />
            )}

            <ClubRegisterModal
                isOpen={showClubModal}
                onClose={() => setShowClubModal(false)}
                storeSlug={storeSlug}
                storeName={store.name}
                benefit={store.club_benefit}
                isGuest={isClubGuest}
                user={clubUser}
                onLogin={handleClubLogin}
            />

            <ShareMenu
                isOpen={showShareMenu}
                onClose={() => setShowShareMenu(false)}
                url={shareUrl}
                title={store.name ? `${store.name} — המקומית` : 'חנות במקומית'}
                heading="שתפו את החנות"
            />
        </div>
        </OfferingTypeProvider>
    );
};

export default StoreView;
